import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { LoginPage, RegistrationPage } from './onboarding';
import DashboardPage from './pages/DashboardPage';
import HomePage from './pages/HomePage';
import PdfViewerPage from './pages/PdfViewerPage';
import ContactUsPage from './pages/ContactUsPage';
import ProfilePage from './pages/ProfilePage';
import ReportsPage from './pages/ReportsPage';
import ProtectedRoute from './components/ProtectedRoute';
import { RegistrationData } from './types/registration';
import { signInUser, signOutUser, AuthUser, getUserProfile, syncAuthTokenClaims } from './services/authService';
import { syncSubscriptionStatus } from './services/stripeService';
import { cacheUserData, clearAllUserCaches } from './services/cacheService';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './config/firebase';
import { useConnectionStatus } from './hooks/useConnectionStatus';
import { useModalKeyboardShortcuts } from './hooks/useModalKeyboardShortcuts';
import { logger } from './utils/logger';
import { useLanguage } from './contexts/LanguageContext';
import { EngineeringLoadingScreen } from './components/EngineeringLoadingScreen';
import { Language } from './constants/strings';

export default function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const isOnline = useConnectionStatus();
    const { strings, setLanguage } = useLanguage();
    useModalKeyboardShortcuts();

    // Listen for authentication state changes and load user language
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUser({
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    emailVerified: user.emailVerified
                });
                setIsLoggedIn(true);

                try {
                    await syncAuthTokenClaims(true);
                } catch (syncError) {
                    logger.warn('[App] Auth claims sync failed on login', syncError);
                }

                // Load and set language from user profile
                try {
                    const userProfile = await getUserProfile(user.uid);
                    if (userProfile && userProfile.language) {
                        const userLanguage = userProfile.language as Language;
                        if (userLanguage === 'en' || userLanguage === 'et' || userLanguage === 'ru') {
                            setLanguage(userLanguage);
                            logger.log('[App] Language set from user profile:', userLanguage);
                        }
                    }
                } catch (error) {
                    logger.error('[App] Error loading user language:', error);
                }
            } else {
                setCurrentUser(null);
                setIsLoggedIn(false);
            }
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [setLanguage]);

    

    // Handle logout when internet connection is lost
    useEffect(() => {
        if (!isOnline && isLoggedIn) {
            // Automatically logout user when connection is lost
            const performLogout = async () => {
                try {
                    // Clear all caches on logout
                    clearAllUserCaches();
                    const keys = Object.keys(sessionStorage);
                    keys.forEach(key => {
                        if (key.startsWith('subscription_sync_') || key.startsWith('dashboard_cache_')) {
                            sessionStorage.removeItem(key);
                        }
                    });
                    
                    await signOutUser();
                    setCurrentUser(null);
                    setIsLoggedIn(false);
                    logger.log('User logged out due to connection loss');
                } catch (error) {
                    logger.error('Error during automatic logout:', error);
                }
            };
            
            performLogout();
        }
    }, [isOnline, isLoggedIn]);

    const handleLogin = async (email: string, password: string) => {
        try {
            logger.log('Firebase login attempt:', email);
            const user = await signInUser(email, password);
            logger.log('Login successful:', user);
            setCurrentUser(user);
            setIsLoggedIn(true);
            
            // Get user profile to determine role and redirect accordingly
            const userProfile = await getUserProfile(user.uid);
            if (userProfile && userProfile.role) {
                // Set language from user profile immediately after login
                if (userProfile.language) {
                    const userLanguage = userProfile.language as Language;
                    if (userLanguage === 'en' || userLanguage === 'et' || userLanguage === 'ru') {
                        setLanguage(userLanguage);
                        logger.log('[Login] Language set from user profile:', userLanguage);
                    }
                }
                
                // Cache all user data (images, profiles, tools) after login
                try {
                    await cacheUserData(user.uid);
                    logger.log('[Login] User data cached successfully');
                } catch (cacheError) {
                    logger.warn('[Login] Failed to cache user data, continuing anyway:', cacheError);
                }
                
                // Sync subscription status on login (checkpoint)
                if (userProfile.role.toLowerCase() === 'manager' && userProfile.subscriptionId) {
                    logger.log('[Login] Syncing subscription status on login...');
                    try {
                        const syncResult = await syncSubscriptionStatus(userProfile.subscriptionId);
                        if (syncResult.success) {
                            // Update sync timestamp for this session
                            const subscriptionLastSyncKey = `subscription_sync_${userProfile.subscriptionId}`;
                            sessionStorage.setItem(subscriptionLastSyncKey, Date.now().toString());
                            logger.log('[Login] Subscription status synced successfully');
                            
                            // Reload profile to get updated subscription status
                            const updatedProfile = await getUserProfile(user.uid);
                            if (updatedProfile) {
                                const role = updatedProfile.role.toLowerCase();
                                if (role === 'admin' || role === 'manager') {
                                    navigate('/dashboard');
                                } else if (role === 'user' || role === 'editor') {
                                    navigate('/home');
                                } else {
                                    navigate('/dashboard');
                                }
                                return;
                            }
                        } else {
                            logger.warn('[Login] Subscription sync failed, using cached data:', syncResult.error);
                        }
                    } catch (syncError: any) {
                        // Backend might be unavailable - continue with DB data
                        if (syncError.message?.includes('Failed to fetch') || syncError.message?.includes('ERR_CONNECTION_REFUSED')) {
                            logger.warn('[Login] Backend unavailable, using database subscription status');
                        } else {
                            logger.error('[Login] Error syncing subscription status:', syncError);
                        }
                    }
                }
                
                const role = userProfile.role.toLowerCase();
                if (role === 'admin' || role === 'manager') {
                    navigate('/dashboard');
                } else if (role === 'user' || role === 'editor') {
                    navigate('/home');
                } else {
                    navigate('/dashboard'); // default fallback
                }
            } else {
                navigate('/dashboard'); // default fallback
            }
        } catch (error: any) {
            logger.error('Login error:', error);
            throw error; // Re-throw to let LoginPage handle the error
        }
    };

    const handleLogout = async () => {
        try {
            // Clear all user caches on logout
            clearAllUserCaches();
            const keys = Object.keys(sessionStorage);
            keys.forEach(key => {
                if (key.startsWith('subscription_sync_') || key.startsWith('dashboard_cache_')) {
                    sessionStorage.removeItem(key);
                }
            });
            
            await signOutUser();
            setCurrentUser(null);
            setIsLoggedIn(false);
            navigate('/login');
        } catch (error) {
            logger.error('Logout error:', error);
        }
    };

    const handleSignup = () => {
        navigate('/register');
    };

    const handleRegistration = (data: RegistrationData) => {
        // Handle registration logic
        logger.log('Registration data:', data);
        logger.log('User preferred language:', data.language);
        
        // Note: Redirect is handled in RegistrationPage after auth state updates
        // This callback is just for logging/completing the registration flow
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    const handleContactUs = () => {
        navigate('/contact-us', { state: { from: 'register' } });
    };

    const handleBackFromContact = () => {
        navigate('/register');
    };

    // Show loading spinner while checking authentication
    if (isLoading) {
        return <EngineeringLoadingScreen ariaLabel={strings.LOADING} />;
    }

    return (
        <>
        <Routes>
            {/* Root redirect - public route that handles initial navigation */}
            <Route path="/" element={
                <ProtectedRoute requireAuth={false}>
                    <Navigate to="/home" replace />
                </ProtectedRoute>
            } />
            
            {/* Public routes - accessible without authentication */}
            <Route 
                path="/login" 
                element={
                    <ProtectedRoute requireAuth={false}>
                        <LoginPage 
                            onLogin={handleLogin}
                            onSignup={handleSignup}
                        />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/register" 
                element={
                    <ProtectedRoute requireAuth={false}>
                        <RegistrationPage 
                            onRegistration={handleRegistration}
                            onBackToLogin={handleBackToLogin}
                            onContactUs={handleContactUs}
                        />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/contact-us" 
                element={
                    <ProtectedRoute requireAuth={false}>
                        <ContactUsPage />
                    </ProtectedRoute>
                } 
            />
            
            {/* Protected routes - require authentication */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <DashboardPage onLogout={handleLogout} />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/home" 
                element={
                    <ProtectedRoute>
                        <HomePage onLogout={handleLogout} />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/pdf-viewer" 
                element={
                    <ProtectedRoute>
                        <PdfViewerPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/profile" 
                element={
                    <ProtectedRoute>
                        <ProfilePage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports" 
                element={
                    <ProtectedRoute>
                        <ReportsPage />
                    </ProtectedRoute>
                } 
            />
        </Routes>
        </>
    );
}